import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'

interface ProgramApplicationListItem {
  application_id: number
  session_name: string | null
  applicant_login_id: string | null
  applicant_name: string | null
  phone: string | null
  affiliation_name: string | null
  status: string
}

const STATUS_STYLE: Record<string, string> = {
  '신청': 'bg-[#fff8e1] text-[#e67e22]',
  '예약완료': 'bg-[#e8f5e9] text-[#388e3c]',
  '신청취소': 'bg-[#f5f5f5] text-[#9e9e9e]',
}

const LIMIT = 20

export default function ProgramApplicationList() {
  const { program_id } = useParams<{ program_id: string }>()
  const navigate = useNavigate()

  const [applications, setApplications] = useState<ProgramApplicationListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [programName, setProgramName] = useState('')
  const [skip, setSkip] = useState(0)
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
  const [excelLoading, setExcelLoading] = useState(false)

  const fetchApplications = useCallback(async (skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))

      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications?${params.toString()}`)
      )
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setApplications(data.applications)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [program_id])

  const fetchProgramName = useCallback(async () => {
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/program/${program_id}`))
      if (!res.ok) return
      const data = await res.json()
      setProgramName(data.title ?? '')
    } catch {
      // 프로그램명 못가져와도 무시
    }
  }, [program_id])

  useEffect(() => {
    fetchProgramName()
  }, [fetchProgramName])

  useEffect(() => {
    fetchApplications(skip)
  }, [skip, fetchApplications])

  const handleApprove = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('신청을 확인완료 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/${id}/approve`),
        { method: 'POST' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchApplications(skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleCancel = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('신청을 취소 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/${id}/cancel`),
        { method: 'POST' }
      )
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchApplications(skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleExcelDownload = async () => {
    setExcelLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(
        getApiUrl(`/marubit/api/program/admin/${program_id}/applications/excel`),
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (!res.ok) throw new Error('다운로드 실패')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `program_applications_${program_id}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('엑셀 다운로드에 실패했습니다.')
    } finally {
      setExcelLoading(false)
    }
  }

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">프로그램 신청내역</h1>
            {programName && (
              <p className="text-[13px] text-[#887267] font-medium">{programName}</p>
            )}
            <p className="text-[13px] text-gray-400 mt-0.5">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/program')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >← 목록으로</button>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex items-center justify-between">
          <span className="text-[13px] text-gray-400">
            총 <span className="text-[#887267] font-bold">{total}</span>건
          </span>
          <button
            onClick={handleExcelDownload}
            disabled={excelLoading}
            className="bg-[#388e3c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-[#2e7d32] transition-colors disabled:opacity-50 border-none"
          >
            {excelLoading ? '다운로드 중...' : '📥 엑셀로 저장'}
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">회차</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">아이디</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">이름</th>
                  <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[130px]">전화번호</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">소속</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">상태</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]"></th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-300 text-[13px]">
                      신청 내역이 없습니다.
                    </td>
                  </tr>
                ) : applications.map(a => {
                  const statusCls = STATUS_STYLE[a.status] ?? 'bg-gray-100 text-gray-500'
                  const isLoading = actionLoadingId === a.application_id
                  return (
                    <tr
                      key={a.application_id}
                      className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/program/${program_id}/applications/${a.application_id}`)}
                    >
                      <td className="px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">
                        {a.session_name ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">
                        {a.applicant_login_id ?? '비회원'}
                      </td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">
                        {a.applicant_name ?? '-'}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">
                        {a.phone ?? '-'}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">
                        {a.affiliation_name ?? '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${statusCls}`}>
                          {a.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {a.status === '신청' && (
                          <div className="flex gap-1.5">
                            <button
                              onClick={e => handleApprove(e, a.application_id)}
                              disabled={isLoading}
                              className="text-[12px] text-[#388e3c] hover:text-[#2e7d32] border border-[#c8e6c9] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >확인</button>
                            <button
                              onClick={e => handleCancel(e, a.application_id)}
                              disabled={isLoading}
                              className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >취소</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button
              onClick={() => setSkip((currentPage - 2) * LIMIT)}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                  acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                  : <button
                      key={p}
                      onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                      className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                        currentPage === p
                          ? 'bg-[#887267] text-white border-[#887267]'
                          : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                      }`}
                    >{p}</button>
              )}
            <button
              onClick={() => setSkip(currentPage * LIMIT)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >›</button>
          </div>
        )}

      </div>
    </div>
  )
}